import { createIcons, Shield, CheckCircle2, XCircle, FileText, ExternalLink } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { router } from "../router/router.js";

export class AppVerifyReportPage extends HTMLElement {
  private loading: boolean = true;
  private error: string | null = null;
  private reportData: any | null = null;
  private token: string = '';

  connectedCallback() {
    const parts = window.location.pathname.split('/');
    const lastPart = parts[parts.length - 1];
    if (lastPart && lastPart !== 'verify-report') {
      this.token = lastPart;
    }
    
    this.fetchReportData();
  }

  private async fetchReportData() {
    this.loading = true;
    this.error = null;
    this.render();

    try {
      if (!this.token) {
        throw new Error('Token de verificação não informado ou inválido na URL.');
      }

      const p = (window as any).app?.platform;
      const activeClientId = contextStore.get().activeClientId;
      const ctx = { activeClientId, userId: contextStore.get().userId, permissions: contextStore.get().permissions } as any;

      // HACK: na versão atual não temos findByVerificationToken, 
      // então tentamos usar findById presumindo que o token seja o ID por hora
      // Num backend real, haveria um endpoint público
      const report = await p.reportService.getReportById(ctx, this.token as any);
      
      if (!report) {
        throw new Error('DOCUMENTO NÃO ENCONTRADO');
      }
      
      this.reportData = report;
      
    } catch (e: any) {
      this.error = e.message || 'Erro ao validar documento.';
    } finally {
      this.loading = false;
      this.render();
    }
  }

  private openPdf = () => {
    if (this.reportData) {
       router.navigate(`/reports/preview/${this.reportData.id}`);
    }
  };

  private render() {
    let contentHtml = '';

    if (this.loading) {
      contentHtml = `
        <div style="text-align: center; padding: 4rem; color: var(--color-graphite-400);">
          <div style="display: flex; justify-content: center; margin-bottom: 1rem; color: var(--ia-primary);">
            <i data-lucide="shield" style="width: 48px; height: 48px;"></i>
          </div>
          <h3>Validando Assinatura Digital...</h3>
          <p>Verificando integridade do documento no banco de dados.</p>
        </div>
      `;
    } else if (this.error) {
      contentHtml = `
        <div style="background-color: #FEE2E2; color: #991B1B; border: 1px solid #FECACA; display: flex; align-items: center; justify-content: center; padding: 1.25rem; border-radius: 8px; margin-bottom: 2rem; font-weight: 600; font-size: 1.125rem; gap: 0.75rem;">
          <i data-lucide="x-circle"></i>
          ${this.error}
        </div>
        <p style="text-align: center; color: var(--color-graphite-400);">Por favor, verifique o QR Code ou o link fornecido. Se o problema persistir, entre em contato com o responsável técnico do documento.</p>
      `;
    } else if (this.reportData) {
      const rep = this.reportData;
      const isCompleted = rep.status === 'COMPLETED' || rep.status === 'SIGNED';

      const bannerHtml = isCompleted
        ? `<div style="background-color: #DCFCE7; color: #166534; border: 1px solid #BBF7D0; display: flex; align-items: center; justify-content: center; padding: 1rem; border-radius: 6px; margin-bottom: 2rem; font-weight: 600; font-size: 1.125rem; gap: 0.75rem;">
            <i data-lucide="check-circle-2"></i> DOCUMENTO VÁLIDO E AUTENTICADO
          </div>`
        : `<div style="background-color: #FEF3C7; color: #92400E; border: 1px solid #FDE68A; display: flex; align-items: center; justify-content: center; padding: 1rem; border-radius: 6px; margin-bottom: 2rem; font-weight: 600; font-size: 1.125rem; gap: 0.75rem;">
            <i data-lucide="alert-triangle"></i> DOCUMENTO EM ELABORAÇÃO OU REVOGADO
          </div>`;

      contentHtml = `
        ${bannerHtml}
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin-bottom: 2rem; background: #F8FAFC; padding: 1.5rem; border-radius: 8px; border: 1px solid #E2E8F0;">
          <div style="display: flex; flex-direction: column;">
            <div style="font-size: 0.75rem; color: var(--color-graphite-400); text-transform: uppercase; font-weight: 700; margin-bottom: 0.25rem;">Relatório Inspect IA Nº</div>
            <div style="font-size: 1rem; color: #0F172A; font-weight: 600;">${rep.reportNumber || rep.id}</div>
          </div>
          <div style="display: flex; flex-direction: column;">
            <div style="font-size: 0.75rem; color: var(--color-graphite-400); text-transform: uppercase; font-weight: 700; margin-bottom: 0.25rem;">Revisão</div>
            <div style="font-size: 1rem; color: #0F172A; font-weight: 600;">${rep.revision || '00'}</div>
          </div>
          <div style="display: flex; flex-direction: column;">
            <div style="font-size: 0.75rem; color: var(--color-graphite-400); text-transform: uppercase; font-weight: 700; margin-bottom: 0.25rem;">Código de Autenticidade</div>
            <div style="font-size: 1rem; color: #0F172A; font-weight: 600;">${rep.verificationCode || 'N/D'}</div>
          </div>
          <div style="display: flex; flex-direction: column;">
            <div style="font-size: 0.75rem; color: var(--color-graphite-400); text-transform: uppercase; font-weight: 700; margin-bottom: 0.25rem;">Data de Emissão</div>
            <div style="font-size: 1rem; color: #0F172A; font-weight: 600;">${new Date(rep.updatedAt || rep.createdAt).toLocaleDateString('pt-BR')}</div>
          </div>
          <div style="display: flex; flex-direction: column; grid-column: span 2;">
            <div style="font-size: 0.75rem; color: var(--color-graphite-400); text-transform: uppercase; font-weight: 700; margin-bottom: 0.25rem;">Token Público / Hash</div>
            <div style="font-size: 0.875rem; color: #0F172A; font-weight: 500; font-family: monospace; word-break: break-all;">
              ${rep.verificationToken || rep.id}
            </div>
          </div>
        </div>

        <div style="display: flex; justify-content: center; margin-top: 2rem; padding-top: 2rem; border-top: 1px solid #E2E8F0;">
          <button id="btn-open-pdf" style="display: inline-flex; align-items: center; justify-content: center; padding: 0.75rem 1.5rem; background-color: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); font-weight: 600; border-radius: 6px; border: none; cursor: pointer; font-size: 1rem; transition: background-color 0.2s; gap: 0.75rem;">
            <i data-lucide="external-link" style="width: 20px; height: 20px;"></i>
            VISUALIZAR DOCUMENTO DIGITAL (READ-ONLY)
          </button>
        </div>
      `;
    }

    this.innerHTML = `
      <div style="min-height: 100vh; background-color: #F1F5F9; padding: 3rem 1rem; font-family: var(--font-sans, 'Inter', sans-serif);">
        <div style="max-width: 700px; margin: 0 auto; background: white; border-radius: 12px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1); overflow: hidden;">
          <div style="background: var(--ia-bg-panel, #0F172A); color: white; padding: 2rem; text-align: center;">
            <div style="display: inline-flex; align-items: center; justify-content: center; width: 64px; height: 64px; background: rgba(255,255,255,0.1); border-radius: 16px; margin-bottom: 1rem;">
              <i data-lucide="file-text" style="width: 32px; height: 32px;"></i>
            </div>
            <h1 style="margin: 0; font-size: 1.5rem; font-weight: 600; letter-spacing: -0.025em;">Verificação de Autenticidade</h1>
            <p style="margin: 0.5rem 0 0 0; color: var(--ia-fg-soft, #94A3B8); font-size: 0.9rem;">Portal Público INSPECT IA</p>
          </div>
          <div style="padding: 2.5rem;">
            ${contentHtml}
          </div>
        </div>
      </div>
    `;

    createIcons({
      icons: { Shield, CheckCircle2, XCircle, FileText, ExternalLink },
      nameAttr: "data-lucide",
      attrs: {
        class: "lucide-icon"
      }
    });

    const btnOpen = this.querySelector('#btn-open-pdf');
    if (btnOpen) {
      btnOpen.addEventListener('click', this.openPdf);
    }
  }
}

customElements.define("app-verify-report-page", AppVerifyReportPage);
